<template>
  <div class="table-wrapper">
    <template v-if="loading">
      <div class="status-card">
        <div class="status-title">Cargando usuarios…</div>
        <div class="status-sub">Estamos preparando la información</div>
      </div>
    </template>

    <template v-else-if="error">
      <div class="status-card error">
        <div class="status-title">{{ error }}</div>
        <div class="status-sub">Intenta nuevamente más tarde</div>
      </div>
    </template>

    <template v-else-if="!rows?.length">
      <div class="status-card">
        <div class="status-title">No hay usuarios registrados</div>
        <div class="status-sub">Cuando se registren, aparecerán aquí</div>
      </div>
    </template>

    <template v-else>
      <div class="table-card">
        <table class="table">
          <thead>
            <tr>
              <th>Nombres</th>
              <th>Apellidos</th>
              <th>Rol</th>
              <th>Fecha de registro</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="user in paginatedRows" :key="user._id">
              <td class="cell-strong">
                <div class="main-text">{{ user.nombres }}</div>
              </td>
              <td>
                <div class="main-text">{{ user.apellidos }}</div>
              </td>
              <td>
                <span class="role-badge">
                  {{ user.rol || '—' }}
                </span>
              </td>
              <td>
                <div class="sub-text">{{ user.fechaRegistro }}</div>
              </td>
            </tr>
          </tbody>
        </table>

        <div class="pagination">
          <button
            class="page-btn prev"
            :disabled="currentPage === 1"
            @click="goPrev"
          >
            Anterior
          </button>

          <div class="page-info">
            Página {{ currentPage }} de {{ totalPages }}
          </div>

          <button
            class="page-btn next"
            :disabled="currentPage === totalPages"
            @click="goNext"
          >
            Siguiente
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";

const PAGE_SIZE = 15;

const props = defineProps({
  rows: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: null,
  },
});

const currentPage = ref(1);

const totalPages = computed(() => {
  if (!props.rows.length) return 1;
  return Math.ceil(props.rows.length / PAGE_SIZE);
});

const paginatedRows = computed(() => {
  const start = (currentPage.value - 1) * PAGE_SIZE;
  const end = start + PAGE_SIZE;
  return props.rows.slice(start, end);
});

function goPrev() {
  if (currentPage.value > 1) {
    currentPage.value -= 1;
  }
}

function goNext() {
  if (currentPage.value < totalPages.value) {
    currentPage.value += 1;
  }
}
</script>

<style scoped>
.table-card {
  width: 100%;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 16px 32px rgba(16,24,40,.06);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.9rem;
  line-height: 1.45;
  color: #1f2937;
}

.table thead {
  background: #f9fafb;
}

.table thead tr th {
  text-align: left;
  font-weight: 600;
  font-size: 0.7rem;
  line-height: 1.2;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  padding: 0.9rem 1rem;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
}

.table tbody tr td {
  padding: 1rem;
  background-color: #fff;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
  font-size: 0.9rem;
  color: #111827;
}

.table tbody tr {
  transition: background-color .14s ease, box-shadow .14s ease;
}

.table tbody tr:hover {
  background-color: #f9fafb;
  box-shadow: 0 8px 20px rgba(16,24,40,.05);
}

.cell-strong .main-text {
  font-weight: 600;
  color: #111827;
}

.main-text {
  font-weight: 500;
  color: #1f2937;
  line-height: 1.4;
}

.sub-text {
  color: #6b7280;
  font-size: 0.8rem;
  line-height: 1.4;
  font-weight: 400;
}

.role-badge {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.35rem 0.6rem;
  font-size: 0.7rem;
  line-height: 1.2;
  font-weight: 500;
  background-color: #eef2ff;
  color: #4f46e5;
  border: 1px solid rgba(99,102,241,.18);
  min-width: 70px;
  justify-content: center;
  text-transform: capitalize;
}

.pagination {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.9rem 1rem;
  border-top: 1px solid #e5e7eb;
  flex-wrap: wrap;
  display: flex;
  justify-content: center;
}

.page-info {
  font-size: 0.85rem;
  color: #111827;
  font-weight: 500;
}

.page-btn {
  appearance: none;
  border: 0;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 500;
  line-height: 1.2;
  padding: 0.6rem 0.9rem;
  cursor: pointer;
  min-width: 90px;
  text-align: center;
  transition: all .12s ease;
  color: #fff;
}

.page-btn.prev {
  background: #ef4444;
  color: #fff;
}

.page-btn.prev:disabled {
  background: #e5e7eb;
  color: #9ca3af;
  cursor: not-allowed;
}

.page-btn.next {
  background: #ef4444;
  color: #fff;
}

.page-btn.next:disabled {
  background: #fecaca;
  color: #7f1d1d;
  cursor: not-allowed;
}

.page-btn:not(:disabled):hover {
  background: #dc2626;
}

.status-card {
  width: 100%;
  min-height: 160px;
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 12px;
  padding: 2rem 1.5rem;
  text-align: center;
  box-shadow: 0 12px 32px rgba(16,24,40,.06);
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 1rem 0;
}

.status-card.error {
  border-color: #ef4444;
  background: #fef2f2;
  box-shadow: 0 12px 32px rgba(220,38,38,.08);
}

.status-title {
  color: #111827;
  font-size: .95rem;
  font-weight: 600;
  line-height: 1.4;
}

.status-card.error .status-title {
  color: #b91c1c;
}

.status-sub {
  margin-top: .4rem;
  color: #6b7280;
  font-size: .8rem;
  line-height: 1.4;
}
</style>
